import { router } from "@inertiajs/react";
import { uuidv7 } from "@/lib/uuid";
import { useFailures, type FailureEntry } from "@/stores/failures";
import { csrfToken } from "@/lib/fetchSideEffect";
import { pushActionToast } from "@/stores/actionToast";
import { canEnqueue, enqueue, flushOutbox, onOpRejected, type OpRejection } from "@/lib/syncEngine";
import { reloadApp } from "@/lib/swClient";
import type { BulkFields, EditableFields, MutationArgs, MutationName } from "@/lib/mutators/types";
import { userToday, useTaskStore, viewTasks } from "@/stores/taskStore";
import { listOfKind } from "@/lib/mutators/apply";

type Failure = Omit<FailureEntry, "id">;

function pushFailure(failure: Failure): void {
  useFailures.getState().pushFailure(failure);
}

// ── Outbox ──────────────────────────────────────────────────────────────────
// A change is recorded in the outbox and every view shows it at once from the store; the
// server answers later, and only a refusal comes back here.

// A request sent to the server directly must not overtake older changes still in the outbox.
function afterOutbox<T>(request: () => Promise<T>): Promise<T> {
  return useTaskStore.getState().pending.length > 0 ? flushOutbox().then(request) : request();
}

const SERVER_ERROR_MESSAGE = "Не удалось сохранить изменения: ошибка на сервере";

type RefusalReason = OpRejection["reason"];

// The server refuses an op the same way every time, so its toast offers no retry. Storage
// that would not open is not going to on a retry either; a fresh start of the app may.
function pushRejection(failure: Failure, reason: RefusalReason): void {
  if (reason === "outbox_held") pushFailure(failure);
  else if (reason === "storage_unavailable") pushFailure({ ...failure, retry: () => void reloadApp(), retryLabel: "Перезагрузить" });
  else pushFailure({ mutation: failure.mutation, message: reason === "error" ? SERVER_ERROR_MESSAGE : failure.message });
}

// What a refused op made in this tab tells its caller, by op id. An op merged into a later
// one is looked up by its own id too, so the tab that made it still hears.
const rejectionFeedback = new Map<string, (reason: RefusalReason) => void>();

onOpRejected(({ op, reason, enqueuedHere }) => {
  const feedbacks = [op.id, ...(op.mergedIds ?? [])].flatMap((id) => {
    const feedback = rejectionFeedback.get(id);
    rejectionFeedback.delete(id);
    return feedback ? [feedback] : [];
  });
  if (enqueuedHere) feedbacks[0]?.(reason);
});

function record<N extends MutationName>(name: N, args: MutationArgs[N], onRejected: (reason: RefusalReason) => void): void {
  const unconfirmed = new Set(useTaskStore.getState().pending.flatMap((op) => [op.id, ...(op.mergedIds ?? [])]));
  for (const id of rejectionFeedback.keys()) if (!unconfirmed.has(id)) rejectionFeedback.delete(id);
  rejectionFeedback.set(enqueue(name, args).id, onRejected);
}

// Records the op, or shows the failure with its retry when the outbox cannot take changes
// (signed out, IndexedDB unavailable, another account's unsent changes not answered yet).
function recordOrFail<N extends MutationName>(name: N, args: MutationArgs[N], failure: Failure, onRejected?: () => void): boolean {
  if (!canEnqueue()) {
    pushFailure(failure);
    return false;
  }
  record(name, args, (reason) => {
    pushRejection(failure, reason);
    onRejected?.();
  });
  return true;
}

/** The editable fields of a task, as the pages name them. */
export interface TaskPatch {
  title?: string;
  notes?: string | null;
  list_id?: string;
  due_on?: string | null;
  my_day_on?: string | null;
  recurrence_rule?: string | null;
  reusable?: boolean;
  visible_from?: string | null;
}

const FIELD_NAMES = {
  title: "title",
  notes: "notes",
  list_id: "listId",
  due_on: "dueOn",
  my_day_on: "myDayOn",
  recurrence_rule: "recurrenceRule",
  reusable: "reusable",
  visible_from: "visibleFrom",
} as const satisfies Record<keyof TaskPatch, keyof EditableFields>;

export function opFields(payload: TaskPatch): EditableFields {
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value !== undefined) fields[FIELD_NAMES[key as keyof TaskPatch]] = value;
  }
  return fields as EditableFields;
}

function confirmNow(onSuccess: (() => void | Promise<unknown>) | undefined): void {
  Promise.resolve(onSuccess?.()).catch((err: unknown) => console.error(err));
}

interface ListCreate {
  title: string;
  listId: string;
  parentId?: string | null;
  myDay?: boolean;
  dueOn?: string | null;
  recurrenceRule?: string | null;
  reusable?: boolean;
  via?: undefined;
}

interface QuickCreate {
  title: string;
  myDay?: boolean;
  /** Captured into the inbox by the server's capture rules: POST /quick online, the outbox offline. */
  via: "quick";
}

export type CreateInput = ListCreate | QuickCreate;

interface CreateOpts {
  /** Called when the task, already shown, turns out not to be saved: the caller can give the typed title back. */
  onRejected?: () => void;
}

function createFailure(title: string, input: CreateInput): Failure {
  return {
    mutation: "create",
    message: `Не удалось создать «${title}»`,
    retry: () => {
      createTask(input);
    },
  };
}

function createInList(input: CreateInput, listId: string | undefined, opts: CreateOpts): string {
  const title = input.title.trim();
  const failure = createFailure(title, input);
  if (!listId) {
    pushFailure(failure);
    return "";
  }
  const id = uuidv7();
  const recorded = recordOrFail(
    "createTask",
    {
      id,
      listId,
      parentId: input.via ? null : input.parentId ?? null,
      title,
      dueOn: input.via ? null : input.dueOn ?? null,
      myDayOn: input.myDay ? userToday() : null,
      recurrenceRule: input.via ? null : input.recurrenceRule ?? null,
      ...(!input.via && input.reusable ? { reusable: true } : {}),
    },
    failure,
    opts.onRejected,
  );
  return recorded ? id : "";
}

// /quick is served by QuickController on Inertia redirects; the Quick screen navigates away
// right after submit, so it does not wait for the task to show up anywhere. Inertia 2 has no
// per-visit exception callback: a visit finished with neither success nor errors failed.
function postQuickCapture(input: QuickCreate): string {
  const id = uuidv7();
  const title = input.title.trim();
  const fail = () => pushFailure(createFailure(title, input));
  let answered = false;
  router.post("/quick", { task: { id, title, my_day: !!input.myDay } }, {
    preserveScroll: true,
    preserveState: true,
    onSuccess: () => { answered = true; },
    onError: () => {
      answered = true;
      fail();
    },
    onFinish: (visit) => {
      if (!answered && !visit.cancelled && !visit.interrupted) fail();
    },
  });
  return id;
}

/**
 * Returns the new task's id, or "" when it could not be saved; a failure toast with a
 * retry says so.
 */
export function createTask(input: CreateInput, opts: CreateOpts = {}): string {
  if (input.via !== "quick") return createInList(input, input.listId, opts);
  if (navigator.onLine) return postQuickCapture(input);
  return createInList(input, listOfKind(useTaskStore.getState(), "inbox")?.id, opts);
}

/** The row the user clicked: a recurring one completes the occurrence it shows. */
interface ToggleSubject {
  id: string;
  status: "pending" | "completed";
  dueOn?: string | null;
  recurrenceRule?: string | null;
}

interface ToggleOpts {
  /** Called once the toggle is recorded in the outbox; useful for refreshing side-channel state. */
  onSuccess?: () => void | Promise<unknown>;
  /** Called when the recorded toggle turns out not to be saved. */
  onRejected?: () => void;
}

// A recurring task completes only the occurrence on screen, unsent completions included;
// before the local copy has the row, the occurrence is the clicked one.
function occurrenceSeen(task: ToggleSubject): { expectedDueOn?: string | null } {
  const row = viewTasks(useTaskStore.getState(), userToday())[task.id] ?? task;
  return row.recurrenceRule ? { expectedDueOn: row.dueOn ?? null } : {};
}

export function toggleTask(task: ToggleSubject, opts: ToggleOpts = {}) {
  const failure: Failure = {
    mutation: "toggle",
    message: task.status === "completed" ? "Не удалось вернуть в активные" : "Не удалось завершить",
    retry: () => { toggleTask(task, opts); },
  };
  const recorded = task.status === "completed"
    ? recordOrFail("uncompleteTask", { id: task.id }, failure, opts.onRejected)
    : recordOrFail("completeTask", { id: task.id, ...occurrenceSeen(task) }, failure, opts.onRejected);
  if (recorded) confirmNow(opts.onSuccess);
}

export function deleteTask(id: string) {
  recordOrFail("deleteTask", { id }, {
    mutation: "delete",
    message: "Не удалось удалить",
    retry: () => { deleteTask(id); },
  });
}

// ── Single-field updates & reorder ──────────────────────────────────────────────
// Shared by every page/panel that edits a task's title/notes/list/due date/My Day
// flag, or drags a row within its siblings.

interface UpdateOpts {
  /** Called once the change is recorded in the outbox. */
  onSuccess?: () => void;
  /** Called when the recorded change turns out not to be saved. */
  onRejected?: () => void;
}

export function updateTask(id: string, payload: TaskPatch, opts: UpdateOpts = {}) {
  const failure: Failure = {
    mutation: "update",
    message: "Не удалось сохранить изменения",
    retry: () => { updateTask(id, payload, opts); },
  };
  if (recordOrFail("updateTask", { id, fields: opFields(payload) }, failure, opts.onRejected)) opts.onSuccess?.();
}

interface ReorderNeighbors {
  before_id: string | null;
  after_id: string | null;
}

interface ReorderOpts {
  /** Called once the change is recorded in the outbox. */
  onSuccess?: () => void;
  /** Called on failure — callers use this to roll back their optimistic local order. */
  onError?: () => void;
}

// Failures here stay silent (no toast): the caller's local optimistic order snaps
// back, which already communicates "the drag didn't stick".
function recordReorder<N extends "reorderTask" | "reorderMyDay">(name: N, args: MutationArgs[N], opts: ReorderOpts): void {
  if (!canEnqueue()) {
    opts.onError?.();
    return;
  }
  record(name, args, () => opts.onError?.());
  opts.onSuccess?.();
}

export function reorderTask(id: string, neighbors: ReorderNeighbors, opts: ReorderOpts = {}) {
  recordReorder("reorderTask", { id, beforeId: neighbors.before_id, afterId: neighbors.after_id }, opts);
}

// My Day's own root-level order (my_day_position) — a distinct ordering axis from
// reorderTask's list/parent-scoped `position` above.
export function reorderMyDayTask(
  id: string,
  placement: ReorderNeighbors & { sequential?: boolean; date?: string },
  opts: ReorderOpts = {},
) {
  const { before_id: beforeId, after_id: afterId, sequential } = placement;
  const date = placement.date ?? userToday();
  recordReorder("reorderMyDay", { id, beforeId, afterId, sequential, date }, opts);
}

// ── Bulk actions (multi-select) ────────────────────────────────────────────────
// One op for the whole selection.

export type BulkPayload = Pick<TaskPatch, "due_on" | "my_day_on" | "list_id">;

interface BulkOpts {
  /** Called once the change is recorded in the outbox. */
  onSuccess?: () => void;
}

export function bulkPatchTasks(ids: string[], payload: BulkPayload, opts: BulkOpts = {}) {
  if (ids.length === 0) return;
  const failure: Failure = {
    mutation: "bulk",
    message: "Не удалось применить к задачам",
    retry: () => bulkPatchTasks(ids, payload, opts),
  };
  if (recordOrFail("bulkPatch", { ids, fields: opFields(payload) as BulkFields }, failure)) opts.onSuccess?.();
}

export function bulkDeleteTasks(ids: string[], opts: BulkOpts = {}) {
  if (ids.length === 0) return;
  const failure: Failure = {
    mutation: "bulk",
    message: "Не удалось удалить задачи",
    retry: () => bulkDeleteTasks(ids, opts),
  };
  if (recordOrFail("bulkDelete", { ids }, failure)) opts.onSuccess?.();
}

// ── AI actions ────────────────────────────────────────────────────────────────
// One generic pipeline for every AI action. The server answers with a fixed
// {operations:[…]} envelope; the frontend previews (edit), applies, and can undo it.
// `op` shapes mirror the server verbatim so operations round-trip without remapping.

/** One proposed operation from an AI action's preview — the editable unit shown before apply. */
export type PreviewOp =
  | { op: "create_subtask"; title: string }
  | { op: "delete"; task_id: string; title: string }
  | { op: "rename"; task_id: string; title: string; new_title: string }
  | { op: "set_notes"; task_id: string; title: string; notes: string }
  | { op: "complete"; task_id: string; title: string }
  | { op: "uncomplete"; task_id: string; title: string }
  | { op: "add_to_my_day"; task_id: string; title: string }
  | { op: "remove_from_my_day"; task_id: string; title: string }
  | { op: "move"; task_id: string; title: string; bucket: "inbox" | "later"; bucket_name: string }
  | { op: "reorder"; items: { task_id: string; title: string }[] };

/** Where an AI action runs: a single task (focus + its subtasks) or a list (its root tasks). */
export type AiAnchor = { kind: "task" | "list"; id: string };

interface UndoState {
  created_ids: string[];
  restore: Record<string, unknown>[];
}

function aiUrl(anchor: AiAnchor, action: "preview" | "apply" | "run"): string {
  const base = anchor.kind === "list" ? `/lists/${anchor.id}` : `/tasks/${anchor.id}`;
  return `${base}/ai_${action}`;
}

async function postJson(url: string, body: unknown): Promise<Response> {
  return afterOutbox(() => fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify(body),
  }));
}

export async function aiUndo(undo: UndoState): Promise<void> {
  await postJson("/tasks/ai_undo", undo);
  router.reload();
}

function pushAiResultToast(anchor: AiAnchor, undo: UndoState, onChange?: () => void) {
  const changed = (undo.created_ids?.length ?? 0) + (undo.restore?.length ?? 0);
  const key = `ai:${anchor.kind}:${anchor.id}`;
  if (changed === 0) {
    pushActionToast({ message: "Изменений нет", actionLabel: "OK", key, onAction: () => {} });
  } else {
    pushActionToast({
      message: "Готово",
      actionLabel: "Отменить",
      key,
      onAction: () => aiUndo(undo).then(() => onChange?.()),
    });
  }
}

function pushAiFailure(retry: () => void) {
  pushFailure({
    mutation: "ai",
    message: "Не получилось — попробуй ещё",
    retry,
  });
}

/** confirm flow, step 1: run the LLM and get the proposed operations to edit. null on error. */
export async function aiPreview(anchor: AiAnchor, actionId?: string): Promise<PreviewOp[] | null> {
  try {
    const res = await postJson(aiUrl(anchor, "preview"), { action_id: actionId });
    if (!res.ok) throw new Error("preview failed");
    const { operations } = await res.json();
    const ops: PreviewOp[] = operations ?? [];
    if (ops.length === 0) {
      pushActionToast({
        message: "ИИ не предложил изменений",
        actionLabel: "OK",
        key: `ai:${anchor.kind}:${anchor.id}`,
        onAction: () => {},
      });
    }
    return ops;
  } catch {
    pushAiFailure(() => aiPreview(anchor, actionId));
    return null;
  }
}

/** confirm flow, step 2: execute the (edited) operations — standard toast + Undo. */
export async function aiApply(
  anchor: AiAnchor,
  operations: PreviewOp[],
  opts: { onChange?: () => void } = {},
): Promise<boolean> {
  try {
    const res = await postJson(aiUrl(anchor, "apply"), { operations });
    if (!res.ok) throw new Error("apply failed");
    const undo: UndoState = await res.json();
    router.reload();
    opts.onChange?.();
    pushAiResultToast(anchor, undo, opts.onChange);
    return true;
  } catch {
    pushAiFailure(() => aiApply(anchor, operations, opts));
    return false;
  }
}

// ── Templates ───────────────────────────────────────────────────────────────
// The «Начать» button spawns a fresh working copy of a reusable template into My Day. The copy
// is an ordinary task, so its server-generated ids/positions are unknown up front —
// POST then reload. Undo soft-deletes the copy.

export async function startTemplate(templateId: string, opts: { onDone?: () => void } = {}) {
  try {
    const res = await postJson(`/templates/${templateId}/start`, {});
    if (!res.ok) throw new Error("start failed");
    const { created_id } = await res.json();
    router.reload();
    opts.onDone?.();
    pushActionToast({
      message: "Список готов в «Мой день» — за покупками! 🛒",
      actionLabel: "Отменить",
      key: `start:${templateId}`,
      onAction: () => deleteTask(created_id),
    });
  } catch {
    pushFailure({
      mutation: "start",
      message: "Не удалось начать",
      retry: () => startTemplate(templateId, opts),
    });
  }
}

/** instant path (no confirm): LLM then apply in one call — standard toast + Undo. */
export async function aiRun(
  anchor: AiAnchor,
  actionId: string | undefined,
  opts: { onChange?: () => void } = {},
): Promise<boolean> {
  try {
    const res = await postJson(aiUrl(anchor, "run"), { action_id: actionId });
    if (!res.ok) throw new Error("run failed");
    const undo: UndoState = await res.json();
    router.reload();
    opts.onChange?.();
    pushAiResultToast(anchor, undo, opts.onChange);
    return true;
  } catch {
    pushAiFailure(() => aiRun(anchor, actionId, opts));
    return false;
  }
}
